import { Badge, InfoHint } from '../../components/ui';
import { MOVED_LABELS } from './dashboardWork';
import { GROUP_HINTS } from './metricHints';
import { dayLabel } from './trackerFormat';
import { kpiValueClass } from './kpiValue';
import type { WhatMoved } from './dashboardWork';

/**
 * Q4 — what actually moved, over the selected range.
 *
 * The one block on this page made of NAMED THINGS rather than counts. That is
 * its whole reason to exist: "Awaiting 1st Touch: 41" never said who, and
 * getting from a number to the work meant leaving the page and building a
 * filter — the thing Kyu's framing rules out.
 *
 * It follows the range control now rather than a fixed seven days, so the
 * heading says which range it is showing. A block with its own private window
 * sitting under a control that changes every other window is a block somebody
 * has to be told about.
 */
export function WhatMovedBlock({
  moved,
  rangeLabel,
  spanLabel,
  dealsVisible,
  onOpenLead,
}: {
  moved: WhatMoved;
  rangeLabel: string;
  spanLabel: string;
  /**
   * False when this person cannot read `deals`. The conversions line is then
   * absent rather than empty — see useRecentDeals for why that distinction is
   * the whole reason this prop exists.
   */
  dealsVisible: boolean;
  onOpenLead: (id: string) => void;
}) {
  return (
    <section className="dash-card dash-moved">
      <header className="dash-card-head">
        <h3 className="dash-card-title">
          What moved
          <InfoHint label="What moved" text={GROUP_HINTS.whatMoved} />
        </h3>
        <p className="dash-card-span">
          {rangeLabel} · {spanLabel}
        </p>
      </header>

      <div className="dash-metric-grid">
        <div className="tracker-kpi-card">
          <div className="tracker-kpi-label">Replies</div>
          <div className={kpiValueClass(moved.counts.reply)}>{moved.counts.reply}</div>
        </div>
        <div className="tracker-kpi-card">
          <div className="tracker-kpi-label">Meetings booked</div>
          <div className={kpiValueClass(moved.counts.meeting)}>{moved.counts.meeting}</div>
        </div>
        {dealsVisible && (
          <div className="tracker-kpi-card">
            {/* "Converted", not "Converted to deal": the badge on each event
                already says that, and the same words labelling both a count
                and an event made the block ambiguous to read and to test. */}
            <div className="tracker-kpi-label">Converted</div>
            <div className={kpiValueClass(moved.counts.converted)}>{moved.counts.converted}</div>
          </div>
        )}
      </div>

      {moved.total === 0 ? (
        <p className="tracker-field-note dash-moved-empty">Nothing moved in this range.</p>
      ) : (
        <ul className="tracker-activity-list dash-moved-list">
          {moved.events.map((event) => (
            <li key={`${event.leadId}:${event.kind}:${event.on}`} className="tracker-activity-row">
              <Badge tone={event.kind === 'converted' ? 'brand' : 'neutral'}>
                {MOVED_LABELS[event.kind]}
              </Badge>
              <button
                type="button"
                className="tracker-lead-link tracker-activity-name"
                onClick={() => {
                  onOpenLead(event.leadId);
                }}
              >
                {event.name}
              </button>
              <span className="tracker-activity-meta">
                {event.company === '' ? '' : `${event.company} · `}
                {/* dayLabel, not shortDate: `on` is a day computed in UTC, and
                    shortDate would render it in the browser's zone — one day
                    early for every reader west of Greenwich. */}
                {dayLabel(event.on)}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* A capped list that does not say what it capped reads as "this is all
          of it". Newly load-bearing: the range now goes up to a year, where
          the events behind these counts run to hundreds. There is no "see them
          all" link because there is no screen that lists them — saying so is
          better than linking somewhere that does not answer it. */}
      {moved.total > moved.events.length && (
        <p className="tracker-field-note">
          Showing the {moved.events.length} most recent of {moved.total}.
        </p>
      )}

      {!dealsVisible && (
        // Said out loud rather than shown as a zero. A sales member without
        // the Deals module reads nothing from `deals`, and an empty line would
        // tell them nothing converted in this range.
        <p className="tracker-field-note">
          Conversions are not shown — they need Deals module access.
        </p>
      )}
    </section>
  );
}
